import React, { useEffect, useMemo, useState } from "react";
import { kFormatter } from "../../../lib/tierHelper";
import { getClassColor, getFormattedRole, getIsHealer, getSpecTreeAccessor } from "../../../lib/wowHelpers";
import { getTierArrowMovedIcon } from "./TierListHelper";
import { useQueryString } from "hooks/general-hooks";
import { Link } from "react-router-dom";
import { getFormattedEncounterType } from "../../../lib/wowHelpers";
import classNames from "classnames";

const SpecContainer = (props) => {
  const { data, encounterType } = props;

  const params = useQueryString();
  const classUrl = getSpecTreeAccessor(data.class);
  const specUrl = getSpecTreeAccessor(data.spec);
  const classColor = getClassColor(data.display_name);

  let statLabel = "DPS";
  let statValue = data.dps;
  if (encounterType === "pvp") {
    statLabel = "Avg. Rating";
    statValue = data.average_rating;
  } else if (params.stat === "hps" || (getIsHealer(data.spec) && !params.stat)) {
    statLabel = "HPS";
    statValue = data.hps;
  }

  const containerStyle = {
    border: `1px solid ${classColor}`,
    background: `linear-gradient(90deg, ${classColor} 0%, #21213E 100%)`,
  };

  const linkEncounter = encounterType === "mythic" ? "" : "/" + encounterType;
  const query = useMemo(() => {
    if ("encounter" in params && params["encounter"] === "all_bosses") {
      delete params["encounter"];
    }

    return Object.keys(params)
      .map((k) => `${k}=${params[k]}`)
      .join("&");
  }, [params]);

  const linkPathName =
    encounterType === "pvp" ? `/wow/${specUrl}/${classUrl}/pvp` : `/wow/${specUrl}/${classUrl}/talents${linkEncounter}`;
  return (
    <Link
      to={{
        pathname: linkPathName,
        search: query,
      }}
    >
      <div className="spec-and-dps-container" style={containerStyle}>
        {getTierArrowMovedIcon(data.arrow_indicator) ? (
          <div className="trending-image"> {getTierArrowMovedIcon(data.arrow_indicator)}</div>
        ) : null}

        <div className="spec-image">
          <img
            src={`https://static.bigbrain.gg/wow/spec-icons/${data.class}-${data.spec}.jpg`}
            alt={`${data.spec} ${data.class}`}
          />
        </div>
        <div className="dps-container">
          <div className="dps-value">{encounterType === "pvp" ? parseFloat(statValue) : kFormatter(statValue)}</div>
          <div className="dps-label">{statLabel}</div>
        </div>
      </div>
    </Link>
  );
};

const TierListsGrid = (props) => {
  const { data, role, encounterType } = props;
  const [tierList, setTierList] = useState({});

  useEffect(() => {
    const sorted = { S: [], A: [], B: [], C: [] };
    if (encounterType === "pvp") {
      if (data.length > 0) {
        let sortedData = data;
        sortedData.sort((a, b) => {
          return b.modified_rating - a.modified_rating;
        });
        sortedData.forEach((spec) => {
          const tier = spec.tier;
          sorted[tier].push(spec);
        });
        setTierList(sorted);
      }
    } else {
      if (data.length > 0) {
        data.forEach((spec) => {
          const tier = spec.tier_letter;
          sorted[tier].push(spec);
        });
        setTierList(sorted);
      }
    }
  }, [data]);

  return (
    <div className={classNames("content-section tier-list-grid-container", { "bottom-border": encounterType === "pvp" })}>
      <div className="tier-list-text-container">
        <div className="content-section-header">
          {getFormattedRole(role)} Tier List for {getFormattedEncounterType(encounterType)}
        </div>
        <div className="description">
          {encounterType === "pvp"
            ? `We create our tier list by evaluating ratings from the very best players and factoring in the frequency of each spec's 
            appearances on the repective PvP leaderboards. The tier of each spec is determined by its effectiveness and prevalence at the highest levels of PVP gameplay. Green and Red arrows show movement of the spec's tier ranking compared to all the other specs.`
            : `We create our tier list by evaluating damage and healing scores from the very best players and factoring in the
          frequency of each spec's appearances in the highest rankings of the current ${getFormattedEncounterType(
            encounterType,
          )}${" "}
          season. The tier of each spec is determined by its effectiveness and prevalence at the highest levels of ${" "}
          ${getFormattedEncounterType(
            encounterType,
          )} gameplay. Green and Red arrows show movement of the spec's tier ranking compared to all the other specs.`}
        </div>
      </div>
      {tierList["S"]?.length > 0 ? (
        <div className="tier-container s-tier">
          <div className="tier-label">S</div>
          <div className="specs-container">
            {tierList["S"]?.map((spec, index) => (
              <SpecContainer data={spec} key={index} encounterType={encounterType} />
            ))}
          </div>
        </div>
      ) : null}
      {tierList["A"]?.length > 0 ? (
        <div className="tier-container a-tier">
          <div className="tier-label">A</div>
          <div className="specs-container">
            {tierList["A"]?.map((spec, index) => (
              <SpecContainer data={spec} key={index} encounterType={encounterType} />
            ))}
          </div>
        </div>
      ) : null}
      {tierList["B"]?.length > 0 ? (
        <div className="tier-container b-tier">
          <div className="tier-label">B</div>
          <div className="specs-container">
            {tierList["B"]?.map((spec, index) => (
              <SpecContainer data={spec} key={index} encounterType={encounterType} />
            ))}
          </div>
        </div>
      ) : null}
      {tierList["C"]?.length > 0 ? (
        <div className="tier-container c-tier">
          <div className="tier-label">C</div>
          <div className="specs-container">
            {tierList["C"]?.map((spec, index) => (
              <SpecContainer data={spec} key={index} encounterType={encounterType} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TierListsGrid;
